import React, { useState, useEffect } from 'react';
import { FaFilter, FaBriefcase, FaMapMarkerAlt, FaTimes, FaClock, FaGraduationCap, FaSearch } from 'react-icons/fa';
import '../../pages/SearchJobs.css';
import './FilterSidebar.css';

const FilterSidebar = ({ 
  filters, 
  onFilterChange, 
  isMobileOpen, 
  setIsMobileOpen,
  jobCount,
  urlKeyword = '',
  onKeywordSubmit,
  onClearAllFilters,
}) => {
  const [keywordDraft, setKeywordDraft] = useState(urlKeyword);

  useEffect(() => {
    setKeywordDraft(urlKeyword);
  }, [urlKeyword]);
  const filterOptions = {
    keywords: [], // This will be handled by the search input
    category: [
      {value: 'accounting_finance', label: 'Accounting & Finance'},
      {value: 'data_analyst', label: 'Data Analyst'},
      {value: 'banking', label: 'Banking'},
      {value: 'bpo', label: 'BPO'},
      {value: 'business_development', label: 'Business Development'}, 
      {value: 'customer_support', label: 'Customer Support'},
      {value: 'health_care', label: 'Health Care'},
      {value: 'management', label: 'Management'},
      {value: 'digital_marketing', label: 'Digital Marketing'},
      {value: 'electrical_electronics', label: 'Electrical & Electronics'},
      {value: 'engineering_technology', label: 'Engineering & Technology'},
      {value: 'healthcare', label: 'Healthcare'},
      {value: 'human_resource', label: 'Human Resource'},
      {value: 'it_software', label: 'IT & Software'},
      {value: 'marketing', label: 'Marketing'},
      {value: 'research_development', label: 'Research & Development'},
      {value: 'retail_ecommerce', label: 'Retail & Ecommerce'}
    ],
    jobType: [
      {value: 'full-time', label: 'Full-Time'},
      {value: 'part-time', label: 'Part-Time'},
      {value: 'freelance', label: 'Freelance'},
      {value: 'contract', label: 'Contract'},
      {value: 'internship', label: 'Internship'}
    ],
    datePosted: [
      { value: 'Last Hour', label: 'Last Hour' },
      { value: 'Last 24 hours', label: 'Last 24 hours' },
      { value: 'Last 7 days', label: 'Last 7 days' },
      { value: 'Last 14 days', label: 'Last 14 days' },
      { value: 'Last 30 days', label: 'Last 30 days' },
    ],
  };

  const handleFilterChange = (type, value) => {
    const newFilters = {
      ...filters,
      category: filters.category || [],
      jobType: filters.jobType || [],
      location: filters.location || [],
      datePosted: filters.datePosted || [],
    };

    if (type === 'datePosted') {
      const index = newFilters.datePosted.indexOf(value);
      newFilters.datePosted = index === -1 ? [value] : [];
      onFilterChange(newFilters);
      return;
    }

    const index = newFilters[type].indexOf(value);
    
    if (index === -1) {
      newFilters[type].push(value);
    } else {
      newFilters[type].splice(index, 1);
    }
    
    onFilterChange(newFilters);
  };

  const clearFilters = () => {
    if (onClearAllFilters) {
      onClearAllFilters();
    } else {
      onFilterChange({
        category: [],
        jobType: [],
        location: [],
        datePosted: [],
      });
    }
  };

  const hasActiveFilters = () => {
    const hasKeyword = Boolean(urlKeyword && urlKeyword.length > 0);
    const hasCheckboxFilters =
      (filters.category?.length ?? 0) > 0 ||
      (filters.jobType?.length ?? 0) > 0 ||
      (filters.location?.length ?? 0) > 0 ||
      (filters.datePosted?.length ?? 0) > 0;
    return hasKeyword || hasCheckboxFilters;
  };

  const handleKeywordSubmit = (e) => {
    e.preventDefault();
    onKeywordSubmit?.(keywordDraft.trim());
  };

  const getFilterIcon = (category) => {
    switch (category) {
      case 'keywords':
        return <FaSearch />;
      case 'category':
        return <FaBriefcase />;
      case 'jobType':
        return <FaMapMarkerAlt />;
      case 'datePosted':
        return <FaClock />;
      default:
        return <FaFilter />;
    }
  };

  const getCategoryTitle = (category) => {
    switch (category) {
      case 'jobType':
        return 'Job Type';
      case 'datePosted':
        return 'Date Posted';
      default:
        return category.charAt(0).toUpperCase() + category.slice(1);
    }
  };

  const formatOptionText = (category, option) => {
    // Handle object options (with value and label)
    if (typeof option === 'object' && option !== null) {
      return option.label;
    }
    
    // Handle string options
    if (category === 'jobType') {
      return option.split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    return option;
  };

  return (
    <>
      <button 
        className="mobile-filter-toggle"
        onClick={() => setIsMobileOpen(true)}
      >
        <FaFilter /> Filters
      </button>

      {isMobileOpen && (
        <div 
          className="filter-overlay"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      <aside className={`filters-sidebar ${isMobileOpen ? 'mobile-open' : ''}`}>
        <div className="filters-section">
          <div className="filters-header">
            <h3><FaFilter /> Filters</h3>
            {hasActiveFilters() && (
              <button className="clear-filters" onClick={clearFilters}>
                Clear All
              </button>
            )}
            <button 
              className="mobile-close-btn"
              onClick={() => setIsMobileOpen(false)}
            >
              <FaTimes />
            </button>
          </div>

          <div className="filter-groups-container">
            {/* Search Keywords Section */}
            <div className="filter-group">
              <div className="filter-group-header">
                <h4>
                  <FaSearch />
                  Search Keywords
                </h4>
              </div>
              <form className="filter-search" onSubmit={handleKeywordSubmit}>
                <input
                  type="search"
                  enterKeyHint="search"
                  placeholder="Type and press Enter to search..."
                  value={keywordDraft}
                  onChange={(e) => setKeywordDraft(e.target.value)}
                  className="filter-search-input"
                  aria-label="Search job keywords"
                />
                <button type="submit" className="filter-keyword-submit" aria-label="Run search">
                  Search
                </button>
              </form>
            </div>

            {/* Other Filter Groups */}
            {Object.entries(filterOptions).map(([category, options]) => (
              category !== 'keywords' && (
                <div key={category} className="filter-group">
                  <div className="filter-group-header">
                    <h4>
                      {getFilterIcon(category)}
                      {getCategoryTitle(category)}
                    </h4>
                    {filters[category]?.length > 0 && (
                      <button 
                        className="clear-filter"
                        onClick={() => handleFilterChange(category, filters[category][0])}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <div className="filter-options">
                    {options.map(option => (
                      <label key={typeof option === 'object' ? option.value : option} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={filters[category]?.includes(typeof option === 'object' ? option.value : option)}
                          onChange={() => handleFilterChange(category, typeof option === 'object' ? option.value : option)}
                        />
                        <span className="checkbox-text">
                          {formatOptionText(category, option)}
                        </span>
                        {jobCount?.[category]?.[typeof option === 'object' ? option.value : option] && (
                          <span className="checkbox-count">
                            ({jobCount[category][typeof option === 'object' ? option.value : option]})
                          </span>
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              )
            ))}
          </div>

          <div className="mobile-filter-actions">
            <button 
              className="apply-filters-btn"
              onClick={() => setIsMobileOpen(false)}
            >
              Apply Filters
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default FilterSidebar;
